import React, { useState, useEffect } from "react"

import VideoRecordForm from "./VideoRecordForm"
import VideoUploadStatus from "./VideoUploadStatus"

const VideoUploadPage = ({ videoReview, uploadHash, onComplete }) => {
  const [currentStatus, setCurrentStatus] = useState(videoReview.status)
  const [uploadStarted, setUploadStarted] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [showingSuccessMessage, setShowingSuccessMessage] = useState(false)

  // Check if upload is still available
  const canUpload = videoReview?.canUpload && !uploadStarted && currentStatus === "pending"

  const handleUploadStart = () => {
    setUploadStarted(true)
  }

  const handleUploadProgress = (progress) => {
    setUploadProgress(progress)
  }

  const handleUploadSuccess = () => {
    setCurrentStatus("processing")
    setUploadProgress(100)
  }

  const handleUploadError = () => {
    setUploadStarted(false)
    setUploadProgress(0)
  }

  // Poll for status updates when processing
  useEffect(() => {
    if (currentStatus === "processing" || currentStatus === "uploading") {
      const pollInterval = setInterval(async () => {
        try {
          const response = await fetch(`/video-review/upload/${uploadHash}/status`)
          const data = await response.json()

          if (data.success) {
            setCurrentStatus(data.data.status)

            // Check if processing is complete and trigger review completion
            if (["completed", "approved", "rejected"].includes(data.data.status)) {
              clearInterval(pollInterval)
              setShowingSuccessMessage(true)

              // Show success message for 2 seconds then complete the review
              setTimeout(() => {
                if (onComplete) {
                  onComplete()
                }
              }, 2000)
            } else if (data.data.status === "failed") {
              clearInterval(pollInterval)
            }
          }
        } catch (error) {
          console.error("Status poll error:", error) // eslint-disable-line no-console
        }
      }, 3000) // Poll every 3 seconds

      return () => clearInterval(pollInterval)
    }
  }, [currentStatus, uploadHash, onComplete])

  // Show success message when upload completes
  if (showingSuccessMessage) {
    return (
      <div className="bg-green-50 flex h-full min-h-[600px] items-center justify-center">
        <div className="rounded-lg bg-white p-8 text-center shadow-lg">
          <div className="mb-4 text-6xl">✅</div>
          <h2 className="mb-2 text-2xl font-bold text-green-600">Video Upload Successful!</h2>
          <p className="text-gray-600">Your video review has been uploaded and is being processed.</p>
          <p className="mt-2 text-sm text-gray-500">Completing your review...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full min-h-[600px]">
      {canUpload ? (
        <VideoRecordForm
          uploadHash={uploadHash}
          onUploadStart={handleUploadStart}
          onUploadProgress={handleUploadProgress}
          onUploadSuccess={handleUploadSuccess}
          onUploadError={handleUploadError}
        />
      ) : (
        <div className="flex h-full items-center justify-center bg-gray-200 p-2">
          <VideoUploadStatus
            status={currentStatus}
            videoReview={videoReview}
            uploadStarted={uploadStarted}
            uploadProgress={uploadProgress}
          />
        </div>
      )}
    </div>
  )
}

export default VideoUploadPage
